import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import templatesApi from "api/landlord/templates/api";
import { Template } from "types/landlord/verificationTemplate";
import { components, SingleValueProps, MenuListProps } from "react-select";
import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  TemplateMenuContent,
  TemplateMenuItem
} from "shared/app/VerificationTemplate/components/TemplateMenuContent";

// ─── Context ──────────────────────────────────────────────────────────────────

type TemplateOption = { value: string; label: string };

interface TemplateSelectorContextValue {
  templates: Template[] | undefined;
  currentTemplateId: string | undefined;
  showAll: boolean;
  onShowAll: () => void;
  onSelect: (id: string) => void;
  isLoading: boolean;
}

const TemplateSelectorContext =
  createContext<TemplateSelectorContextValue | null>(null);

const useTemplateSelectorContext = () => {
  const ctx = useContext(TemplateSelectorContext);
  if (!ctx) throw new Error("Must be inside TemplateSelectorContext.Provider");
  return ctx;
};

// ─── Module-level components (stable references) ──────────────────────────────

const CustomSingleValue = (props: SingleValueProps<unknown>) => {
  const { templates } = useTemplateSelectorContext();
  const { value } = props.data as TemplateOption;
  const template = templates?.find((t) => t.id === value);

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <HugeiconsIcon icon={File02Icon} size={20} />
        <span className="truncate">{template?.name}</span>
        <Show if={Boolean(template?.is_property_default)}>
          <span className="shrink-0 text-content-200 text-sm">(Default)</span>
        </Show>
      </div>
    </components.SingleValue>
  );
};

const CustomMenuList = (props: MenuListProps<unknown>) => {
  const {
    templates = [],
    currentTemplateId,
    showAll,
    onShowAll,
    onSelect,
    isLoading
  } = useTemplateSelectorContext();

  const inputValue = (props.selectProps.inputValue ?? "").toLowerCase();

  const { propertyItems, otherItems } = useMemo(() => {
    const toItem = (t: Template): TemplateMenuItem => ({
      id: t.id,
      name: t.name,
      isDefault: Boolean(t.is_property_default),
      isCurrent: t.id === currentTemplateId
    });

    const matchesSearch = (t: Template) =>
      !inputValue || t.name.toLowerCase().includes(inputValue);

    return {
      propertyItems: templates
        .filter((t) => t.is_property_template && matchesSearch(t))
        .map(toItem),
      otherItems: templates
        .filter((t) => !t.is_property_template && matchesSearch(t))
        .map(toItem)
    };
  }, [templates, inputValue, currentTemplateId]);

  if (isLoading)
    return (
      <components.MenuList {...props}>
        <div className="px-4 py-2 text-center text-content-500">Loading...</div>
      </components.MenuList>
    );

  return (
    <components.MenuList {...props}>
      <TemplateMenuContent
        sectionTitle={
          propertyItems.length > 0 && otherItems.length > 0
            ? "Templates for this property"
            : undefined
        }
        items={propertyItems.length > 0 ? propertyItems : otherItems}
        otherItems={propertyItems.length > 0 ? otherItems : []}
        showAll={showAll}
        onShowAll={onShowAll}
        onSelect={onSelect}
      />
    </components.MenuList>
  );
};

const selectorComponents = {
  SingleValue: CustomSingleValue,
  MenuList: CustomMenuList
};

// ─── TemplateSelector ─────────────────────────────────────────────────────────

interface TemplateSelectorProps {
  propertyId?: number;
  defaultTemplateId?: string;
  onTemplateChange: (template: Template | undefined) => void;
}

export const TemplateSelector = ({
  propertyId,
  defaultTemplateId,
  onTemplateChange
}: TemplateSelectorProps) => {
  const { permissions } = useAuthContext();
  const canOverride = Boolean(
    permissions.canOverrideApplicantScreeningTemplate
  );

  const selectRef = useRef<{ blur: () => void } | null>(null);
  const [currentTemplateId, setCurrentTemplateId] = useState<
    string | undefined
  >();
  const [showAll, setShowAll] = useState(false);

  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["TEMPLATES", { property_id: propertyId }],
    queryFn: () =>
      templatesApi
        .getTemplates({ page: 1, per_page: 999, property_id: propertyId })
        .then((res) => res.items),
    enabled: Boolean(propertyId)
  });

  const defaultTemplateData = useMemo(() => {
    if (!templates || !defaultTemplateId) return undefined;
    return templates.find((t) => t.id === defaultTemplateId);
  }, [templates, defaultTemplateId]);

  useEffect(() => {
    if (defaultTemplateId && defaultTemplateData) {
      setCurrentTemplateId(defaultTemplateId);
      onTemplateChange(defaultTemplateData);
    }
    // onTemplateChange intentionally omitted — it's a callback prop, not a trigger condition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTemplateId, defaultTemplateData]);

  useEffect(() => {
    setShowAll(false);
    setCurrentTemplateId(undefined);
  }, [propertyId]);

  const handleTemplateSelect = useCallback(
    (id: string) => {
      const selectedTemplate = templates?.find((t) => t.id === id);
      setCurrentTemplateId(id);
      onTemplateChange(selectedTemplate);
      selectRef.current?.blur();
    },
    [templates, onTemplateChange]
  );

  const options = useMemo(
    () => (templates ?? []).map((t) => ({ value: t.id, label: t.name })),
    [templates]
  );

  const contextValue = useMemo<TemplateSelectorContextValue>(
    () => ({
      templates,
      currentTemplateId,
      showAll,
      onShowAll: () => setShowAll(true),
      onSelect: handleTemplateSelect,
      isLoading: isLoadingTemplates
    }),
    [
      templates,
      currentTemplateId,
      showAll,
      handleTemplateSelect,
      isLoadingTemplates
    ]
  );

  if (!propertyId) return null;

  return (
    <TemplateSelectorContext.Provider value={contextValue}>
      <div className="space-y-2">
        <Select
          ref={selectRef}
          label="Screening template"
          hideOptional
          id="template-selector"
          value={currentTemplateId}
          onChange={() => {}}
          options={options}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          searchable={true}
          components={selectorComponents}
          isDisabled={!canOverride}
          loading={isLoadingTemplates}
        />
      </div>
    </TemplateSelectorContext.Provider>
  );
};
